import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaTrash, FaPlus, FaImage, FaVideo, FaUser, FaUserShield, FaGripVertical, FaLayerGroup } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ImageCropperModal from "./ImageCropperModal";
import FramePositionEditor from "./FramePositionEditor";

/**
 * CarouselMediaConfig - Reusable component for configuring carousel media
 * Used for both Post Carousel and Participation Carousel
 * 
 * @param {Object} config - Current carousel configuration
 * @param {Function} onConfigChange - Callback when configuration changes
 * @param {Function} handleFileUpload - Firebase upload function
 * @param {string} configKey - Key identifier ("postCarousel" or "participationCarousel")
 * @param {boolean} isEditMode - Whether in edit mode
 * @param {Function} onUploadingChange - Callback for upload state
 */
const CarouselMediaConfig = ({
  config = {
    adminMediaCount: 0,
    userMediaCount: 0,
    mediaItems: [],
  },
  onConfigChange,
  handleFileUpload,
  configKey = "postCarousel",
  isEditMode = false,
  onUploadingChange,
  frameImageUrl = null, // Post Frame from Participation tab
}) => {
  const MAX_TOTAL_MEDIA = 5;
  const MIN_MEDIA = 2; // Instagram carousel minimum

  // Local state for counts
  const [adminCount, setAdminCount] = useState(config.adminMediaCount || 0);
  const [userCount, setUserCount] = useState(config.userMediaCount || 0);
  const [mediaItems, setMediaItems] = useState(config.mediaItems || []);
  const [uploadingSlots, setUploadingSlots] = useState(new Set());
  const [localPreviews, setLocalPreviews] = useState({});
  // { slotIndex, userImageUrl } — which slot's frame editor is open
  const [frameEditor, setFrameEditor] = useState(null);

  // Track blob URLs for cleanup
  const blobUrlsRef = useRef([]);

  // Upload count for parent notification
  const [uploadCount, setUploadCount] = useState(0);
  const [cropRequest, setCropRequest] = useState(null);
  const inc = () => setUploadCount((c) => c + 1);
  const dec = () => setUploadCount((c) => Math.max(0, c - 1));

  // Notify parent of upload state
  useEffect(() => {
    if (typeof onUploadingChange === "function") {
      onUploadingChange(uploadCount > 0);
    }
  }, [uploadCount, onUploadingChange]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      });
      blobUrlsRef.current = [];
    };
  }, []);

  // Hydrate from config when it changes (edit mode)
  useEffect(() => {
    if (config) {
      setAdminCount(config.adminMediaCount || 0);
      setUserCount(config.userMediaCount || 0);
      setMediaItems(config.mediaItems || []);
    }
  }, [config]);

  // Create preview URL for file
  const makePreview = (file) => {
    const url = URL.createObjectURL(file);
    blobUrlsRef.current.push(url);
    return url;
  };

  // Get total media count
  const totalCount = adminCount + userCount;

  // Update parent with new config
  const updateConfig = useCallback((newAdminCount, newUserCount, newMediaItems) => {
    if (typeof onConfigChange === "function") {
      onConfigChange({
        adminMediaCount: newAdminCount,
        userMediaCount: newUserCount,
        mediaItems: newMediaItems,
      });
    }
  }, [onConfigChange]);

  // Generate media slots based on admin and user counts
  const generateMediaSlots = useCallback((newAdminCount, newUserCount, existingItems = []) => {
    const total = newAdminCount + newUserCount;
    const newItems = [];

    for (let i = 0; i < total; i++) {
      const isUserSlot = i >= newAdminCount;
      const existingItem = existingItems[i];

      if (existingItem) {
        // Preserve existing item but update isUserMedia based on position
        newItems.push({
          ...existingItem,
          isUserMedia: isUserSlot,
          sequence: i,
        });
      } else {
        // Create new empty slot
        newItems.push({
          url: "",
          mediaType: "IMAGE",
          isUserMedia: isUserSlot,
          sequence: i,
          hasFeatureFrame: false,
          framePosition: { x: 50, y: 50, scale: 1, bgScale: 1 },
        });
      }
    }

    return newItems;
  }, []);

  // Handle admin count change
  const handleAdminCountChange = (value) => {
    const newAdminCount = Math.max(0, Math.min(value, MAX_TOTAL_MEDIA - userCount));
    setAdminCount(newAdminCount);
    const newItems = generateMediaSlots(newAdminCount, userCount, mediaItems);
    setMediaItems(newItems);
    updateConfig(newAdminCount, userCount, newItems);
  };

  // Handle user count change
  const handleUserCountChange = (value) => {
    const newUserCount = Math.max(0, Math.min(value, MAX_TOTAL_MEDIA - adminCount));
    setUserCount(newUserCount);
    const newItems = generateMediaSlots(adminCount, newUserCount, mediaItems);
    setMediaItems(newItems);
    updateConfig(adminCount, newUserCount, newItems);
  };

  // Handle media type change for a slot
  const handleMediaTypeChange = (index, newType) => {
    const newItems = [...mediaItems];
    newItems[index] = {
      ...newItems[index],
      mediaType: newType,
      // Clear URL if type changes
      url: newItems[index].mediaType !== newType ? "" : newItems[index].url,
    };
    setMediaItems(newItems);
    updateConfig(adminCount, userCount, newItems);

    // Clear local preview if type changes
    if (newItems[index].mediaType !== newType) {
      setLocalPreviews((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  };

  // Handle file upload for a slot
  const handleSlotUpload = async (index, file) => {
    if (!file) return;
    
    // Auto-open cropper for standard images to enforce 1:1 format
    if (file.type.startsWith('image/') && !file.type.includes('gif')) {
      setCropRequest({ index, file });
      return;
    }
    
    await proceedWithUpload(index, file);
  };
  
  const proceedWithUpload = async (index, file) => {

    // Validate file type matches selected media type
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    const expectedType = mediaItems[index]?.mediaType;

    if (expectedType === "VIDEO" && !isVideo) {
      alert("Please upload a video file for this slot");
      return;
    }
    if (expectedType === "IMAGE" && !isImage) {
      alert("Please upload an image file for this slot");
      return;
    }

    // Create local preview
    const previewUrl = makePreview(file);
    setLocalPreviews((prev) => ({
      ...prev,
      [index]: previewUrl,
    }));

    // Mark slot as uploading
    setUploadingSlots((prev) => new Set([...prev, index]));

    try {
      inc();
      const uploadedUrl = await handleFileUpload(file);

      if (uploadedUrl) {
        const newItems = [...mediaItems];
        newItems[index] = {
          ...newItems[index],
          url: uploadedUrl,
        };
        setMediaItems(newItems);
        updateConfig(adminCount, userCount, newItems);
      }
    } catch (error) {
      console.error("Upload failed for slot", index, error);
      alert("Upload failed. Please try again.");
      // Clear preview on error
      setLocalPreviews((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    } finally {
      dec();
      setUploadingSlots((prev) => {
        const updated = new Set([...prev]);
        updated.delete(index);
        return updated;
      });
    }
  };

  // Handle removing media from a slot
  const handleRemoveMedia = (index) => {
    const newItems = [...mediaItems];
    newItems[index] = {
      ...newItems[index],
      url: "",
    };
    setMediaItems(newItems);
    updateConfig(adminCount, userCount, newItems);

    // Clear local preview
    setLocalPreviews((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  // Toggle Feature Frame on a user slot (radio: only one active at a time)
  const handleFeatureFrameToggle = (index) => {
    const newItems = mediaItems.map((item, i) => ({
      ...item,
      hasFeatureFrame: i === index ? !item.hasFeatureFrame : false,
    }));
    setMediaItems(newItems);
    updateConfig(adminCount, userCount, newItems);
  };

  // Save frame position from editor
  const handleFramePositionSave = (position) => {
    if (frameEditor === null) return;
    const newItems = mediaItems.map((item, i) =>
      i === frameEditor.slotIndex ? { ...item, framePosition: position } : item
    );
    setMediaItems(newItems);
    updateConfig(adminCount, userCount, newItems);
    setFrameEditor(null);
  };

  // Handle drag end for reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return;

    const newItems = Array.from(mediaItems);
    const [removed] = newItems.splice(sourceIndex, 1);
    newItems.splice(destIndex, 0, removed);

    // Update sequences and isUserMedia based on new positions
    const updatedItems = newItems.map((item, idx) => ({
      ...item,
      sequence: idx,
      isUserMedia: idx >= adminCount,
    }));

    setMediaItems(updatedItems);
    updateConfig(adminCount, userCount, updatedItems);
  };

  // Render media slot
  const renderMediaSlot = (item, index, provided) => {
    const isUploading = uploadingSlots.has(index);
    const previewUrl = localPreviews[index] || item.url;
    const isUserSlot = item.isUserMedia;

    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        className={`
          relative border-2 rounded-lg p-3 transition-all
          ${isUserSlot 
            ? "border-blue-300 bg-blue-50" 
            : "border-green-300 bg-green-50"
          }
          ${isUploading ? "opacity-70" : ""}
        `}
      >
        {/* Drag Handle */}
        <div
          {...provided.dragHandleProps}
          className="absolute top-2 left-2 cursor-grab text-gray-400 hover:text-gray-600"
        >
          <FaGripVertical />
        </div>

        {/* Slot Header */}
        <div className="flex items-center justify-between mb-2 pl-6">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">
              Slot {index + 1}
            </span>
            {isUserSlot ? (
              <span className="flex items-center gap-1 text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                <FaUser className="text-xs" />
                User Media
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                <FaUserShield className="text-xs" />
                Admin Media
              </span>
            )}
          </div>

          {/* Media Type Selector */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleMediaTypeChange(index, "IMAGE")}
              className={`
                flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors
                ${item.mediaType === "IMAGE" 
                  ? "bg-purple-600 text-white" 
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }
              `}
            >
              <FaImage /> Image
            </button>
            <button
              type="button"
              onClick={() => handleMediaTypeChange(index, "VIDEO")}
              className={`
                flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors
                ${item.mediaType === "VIDEO" 
                  ? "bg-purple-600 text-white" 
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }
              `}
            >
              <FaVideo /> Video
            </button>
          </div>
        </div>

        {/* Preview Badge for User Slots */}
        {isUserSlot && previewUrl && (
          <div className="absolute top-2 right-2 z-10">
            <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow">
              Preview Only - User will replace
            </span>
          </div>
        )}

        {/* Feature Frame — user slots only */}
        {isUserSlot && frameImageUrl && (
          <div className="mt-2 pl-6">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 accent-purple-600"
                checked={!!item.hasFeatureFrame}
                onChange={() => handleFeatureFrameToggle(index)}
              />
              <FaLayerGroup className="text-purple-500 text-sm" />
              <span className="text-sm font-medium text-purple-700">Feature Frame</span>
            </label>

            {item.hasFeatureFrame && (
              <div className="mt-2">
                {/* Mini composite preview */}
                <div
                  className="relative w-20 h-20 rounded border-2 border-purple-400 overflow-hidden cursor-pointer hover:ring-2 hover:ring-purple-500 transition"
                  title="Click to adjust position"
                  onClick={() =>
                    setFrameEditor({
                      slotIndex: index,
                      userImageUrl: previewUrl || null,
                    })
                  }
                >
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt="user"
                      className="absolute object-cover"
                      style={{
                        zIndex: 1,
                        width: "100%",
                        height: "100%",
                        left: "50%",
                        top: "50%",
                        transform: `translate(-50%,-50%) scale(${item.framePosition?.bgScale ?? 1})`,
                        transformOrigin: "center center",
                      }}
                    />
                  )}
                  <img
                    src={frameImageUrl}
                    alt="frame"
                    className="absolute object-contain pointer-events-none"
                    style={{
                      zIndex: 2,
                      width: "60%",
                      height: "60%",
                      left: `${item.framePosition?.x ?? 50}%`,
                      top: `${item.framePosition?.y ?? 50}%`,
                      transform: `translate(-50%,-50%) scale(${item.framePosition?.scale ?? 1})`,
                      transformOrigin: "center center",
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-25 transition">
                    <span className="text-white text-xs font-semibold opacity-0 hover:opacity-100">Edit</span>
                  </div>
                </div>
                <p className="text-xs text-purple-500 mt-1">Click preview to adjust position</p>
              </div>
            )}
          </div>
        )}

        {/* Upload Area — 1:1 ratio */}
        <div className="mt-2">
          {previewUrl ? (
            <div className="relative" style={{ aspectRatio: "1/1" }}>
              {item.mediaType === "VIDEO" ? (
                <video
                  src={previewUrl}
                  className="w-full h-full object-cover rounded border"
                  controls
                  muted
                />
              ) : (
                <img
                  src={previewUrl}
                  alt={`Slot ${index + 1}`}
                  className="w-full h-full object-cover rounded border"
                />
              )}
              <button
                type="button"
                onClick={() => handleRemoveMedia(index)}
                className="absolute top-1 right-1 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                disabled={isUploading}
              >
                <FaTrash className="text-xs" />
              </button>
            </div>
          ) : (
            <label
              className={`
                flex flex-col items-center justify-center w-full
                border-2 border-dashed rounded-lg cursor-pointer 
                transition-colors
                ${isUploading 
                  ? "bg-gray-100 border-gray-300" 
                  : "bg-white border-gray-300 hover:border-purple-400 hover:bg-purple-50"
                }
              `}
              style={{ aspectRatio: "1/1" }}
            >
              <input
                type="file"
                className="hidden"
                accept={item.mediaType === "VIDEO" ? "video/*" : "image/*"}
                onChange={(e) => handleSlotUpload(index, e.target.files?.[0])}
                disabled={isUploading}
              />
              {isUploading ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="text-sm text-gray-500 mt-2">Uploading...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center text-gray-500">
                  <FaPlus className="text-2xl mb-2" />
                  <span className="text-sm">
                    Upload {item.mediaType === "VIDEO" ? "Video" : "Image"}
                  </span>
                  <span className="text-xs text-gray-400 mt-1">
                    {isUserSlot ? "(Optional preview for user)" : "(Required)"}
                  </span>
                </div>
              )}
            </label>
          )}
        </div>
      </div>
    );
  };


  const handleCropComplete = async (croppedFile) => {
    const { index } = cropRequest;
    setCropRequest(null);
    await proceedWithUpload(index, croppedFile);
  };

  return (
    <div className="bg-white border rounded-lg p-4 shadow-sm">
      {cropRequest && (
        <ImageCropperModal 
          file={cropRequest.file} 
          onCrop={handleCropComplete} 
          onCancel={() => setCropRequest(null)} 
        />
      )}

      {/* Frame Position Editor modal */}
      {frameEditor !== null && frameImageUrl && (
        <FramePositionEditor
          frameImageUrl={frameImageUrl}
          userImageUrl={frameEditor.userImageUrl}
          initialPosition={
            mediaItems[frameEditor.slotIndex]?.framePosition || { x: 50, y: 50, scale: 1 }
          }
          onSave={handleFramePositionSave}
          onClose={() => setFrameEditor(null)}
        />
      )}
      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        🎠 Carousel Configuration
        <span className="text-sm font-normal text-gray-500">
          (2-5 media items)
        </span>
      </h4>

      {/* Count Configuration */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Admin Media Count */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-green-800 mb-2">
            <FaUserShield />
            Admin Media Count
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleAdminCountChange(adminCount - 1)}
              className="w-8 h-8 rounded bg-green-200 text-green-800 hover:bg-green-300 font-bold disabled:opacity-50"
              disabled={adminCount <= 0}
            >
              -
            </button>
            <input
              type="number"
              value={adminCount}
              onChange={(e) => handleAdminCountChange(parseInt(e.target.value) || 0)}
              min={0}
              max={MAX_TOTAL_MEDIA - userCount}
              className="w-16 h-8 text-center border border-green-300 rounded font-semibold"
            />
            <button
              type="button"
              onClick={() => handleAdminCountChange(adminCount + 1)}
              className="w-8 h-8 rounded bg-green-200 text-green-800 hover:bg-green-300 font-bold disabled:opacity-50"
              disabled={totalCount >= MAX_TOTAL_MEDIA}
            >
              +
            </button>
          </div>
          <p className="text-xs text-green-600 mt-1">
            Media uploaded by admin (mandatory)
          </p>
        </div>

        {/* User Media Count */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-blue-800 mb-2">
            <FaUser />
            User Media Count
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleUserCountChange(userCount - 1)}
              className="w-8 h-8 rounded bg-blue-200 text-blue-800 hover:bg-blue-300 font-bold disabled:opacity-50"
              disabled={userCount <= 0}
            >
              -
            </button>
            <input
              type="number"
              value={userCount}
              onChange={(e) => handleUserCountChange(parseInt(e.target.value) || 0)}
              min={0}
              max={MAX_TOTAL_MEDIA - adminCount}
              className="w-16 h-8 text-center border border-blue-300 rounded font-semibold"
            />
            <button
              type="button"
              onClick={() => handleUserCountChange(userCount + 1)}
              className="w-8 h-8 rounded bg-blue-200 text-blue-800 hover:bg-blue-300 font-bold disabled:opacity-50"
              disabled={totalCount >= MAX_TOTAL_MEDIA}
            >
              +
            </button>
          </div>
          <p className="text-xs text-blue-600 mt-1">
            Media to be uploaded by user
          </p>
        </div>
      </div>

      {/* Total Counter */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 mb-4">
        <span className="text-sm font-medium text-gray-700">
          Total Media Slots:
        </span>
        <span className={`
          text-lg font-bold px-3 py-1 rounded
          ${totalCount === 0 
            ? "bg-gray-200 text-gray-600" 
            : totalCount < MIN_MEDIA 
              ? "bg-yellow-200 text-yellow-800"
              : "bg-green-200 text-green-800"
          }
        `}>
          {totalCount} / {MAX_TOTAL_MEDIA}
        </span>
      </div>

      {/* Warning for Instagram minimum */}
      {totalCount > 0 && totalCount < MIN_MEDIA && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
          ⚠️ Instagram carousels require minimum {MIN_MEDIA} media items. Current: {totalCount}
        </div>
      )}

      {/* Media Slots */}
      {totalCount > 0 && (
        <div className="mt-4">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`carousel-${configKey}`} direction="horizontal">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {/* Admin Media Section */}
                  {adminCount > 0 && (
                    <div className="mb-6">
                      <h5 className="text-sm font-medium text-green-800 mb-3 flex items-center gap-2">
                        <FaUserShield className="text-green-600" />
                        Admin Media Slots
                        <span className="text-xs text-gray-500">(Drag to reorder)</span>
                      </h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {mediaItems
                          .map((item, index) => ({ item, index }))
                          .filter(({ item }) => !item.isUserMedia)
                          .map(({ item, index }) => (
                            <Draggable
                              key={`${configKey}-slot-${index}`}
                              draggableId={`${configKey}-slot-${index}`}
                              index={index}
                            >
                              {(prov) => renderMediaSlot(item, index, prov)}
                            </Draggable>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* User Media Section */}
                  {userCount > 0 && (
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
                        <FaUser className="text-blue-600" />
                        User Media Slots
                        <span className="text-xs text-gray-500">(Preview only — users will replace)</span>
                      </h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {mediaItems
                          .map((item, index) => ({ item, index }))
                          .filter(({ item }) => item.isUserMedia)
                          .map(({ item, index }) => (
                            <Draggable
                              key={`${configKey}-slot-${index}`}
                              draggableId={`${configKey}-slot-${index}`}
                              index={index}
                            >
                              {(prov) => renderMediaSlot(item, index, prov)}
                            </Draggable>
                          ))}
                      </div>
                    </div>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}

      {/* Empty State */}
      {totalCount === 0 && (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
          <p className="text-lg mb-2">🎠</p>
          <p>Configure the number of admin and user media slots above</p>
          <p className="text-sm text-gray-400 mt-1">
            Instagram carousels support 2-10 media items
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
        <p className="font-medium mb-1">ℹ️ How it works:</p>
        <ul className="list-disc list-inside space-y-1 text-purple-700">
          <li><span className="bg-green-200 px-1 rounded">Admin media</span> comes first and is mandatory</li>
          <li><span className="bg-blue-200 px-1 rounded">User media</span> slots are for participants to upload</li>
          <li>You can upload preview images for user slots (users will replace them)</li>
          <li>Drag slots to reorder the carousel sequence</li>
          <li><span className="bg-purple-200 px-1 rounded">Feature Frame</span> overlays the Post Frame on a user slot — only one slot can be active at a time. Click the mini preview to adjust the user image position inside the frame.</li>
        </ul>
      </div>
    </div>
  );
};

export default CarouselMediaConfig;
